// frontend/src/askos/prefsUtils.js
//
// Ported from pages/AskOS.js (the original monolithic AskOS app) — same
// constants/helpers, adapted for the new Samkalp dashboard. Kept as a
// shared module since both AskosChat.js (read-aloud) and SettingsPage.js
// (voice/theme/accessibility tabs) need them.
export const LANG_MAP = {
    en: 'en-IN', ta: 'ta-IN', hi: 'hi-IN', te: 'te-IN',
    ml: 'ml-IN', kn: 'kn-IN', bn: 'bn-IN', mr: 'mr-IN',
};

export const LANGUAGES = [
    { code: 'en', label: 'English',   native: 'English'  },
    { code: 'ta', label: 'Tamil',     native: 'தமிழ்'     },
    { code: 'hi', label: 'Hindi',     native: 'हिन्दी'    },
    { code: 'te', label: 'Telugu',    native: 'తెలుగు'   },
    { code: 'ml', label: 'Malayalam', native: 'മലയാളം'   },
    { code: 'kn', label: 'Kannada',   native: 'ಕನ್ನಡ'    },
    { code: 'bn', label: 'Bengali',   native: 'বাংলা'    },
    { code: 'mr', label: 'Marathi',   native: 'मराठी'    },
];

export const THEMES = [
    { key: 'light',         label: 'Light',        icon: '☀️', desc: 'Default white background'        },
    { key: 'dark',          label: 'Dark',          icon: '🌙', desc: 'Dark background, less eye strain' },
    { key: 'high-contrast', label: 'High Contrast', icon: '⬛', desc: 'Maximum contrast (WCAG AA)'      },
    { key: 'sepia',         label: 'Soft Sepia',    icon: '📜', desc: 'Warm tones, easier on the eyes'  },
];

export const ACCENT_PRESETS = [
    { hex: '#6366f1', label: 'Indigo'  },
    { hex: '#2563eb', label: 'Blue'    },
    { hex: '#10b981', label: 'Emerald' },
    { hex: '#f59e0b', label: 'Amber'   },
    { hex: '#ef4444', label: 'Red'     },
    { hex: '#8b5cf6', label: 'Violet'  },
];

export const FONT_SIZES = [
    { key: 'sm', label: 'Small',   px: '14px' },
    { key: 'md', label: 'Default', px: '16px' },
    { key: 'lg', label: 'Large',   px: '18px' },
    { key: 'xl', label: 'X-Large', px: '20px' },
];

export const LINE_SPACINGS = [
    { key: 'normal',  label: 'Normal',  lh: 1.5  },
    { key: 'relaxed', label: 'Relaxed', lh: 1.75 },
    { key: 'loose',   label: 'Loose',   lh: 2.1  },
];

export const VOICE_SPEEDS = [
    { value: 0.75, label: 'Slow'   },
    { value: 1.0,  label: 'Normal' },
    { value: 1.25, label: 'Fast'   },
];

export const DEFAULT_PREFS = {
    theme: 'light', accent: '#6366f1', fontSize: 'md', lineSpacing: 'normal',
    reduceMotion: false, dyslexiaFont: false, focusIndicators: false,
    voiceEnabled: false, voiceSpeed: 1.0, voiceType: 'female', voiceName: null,
    language: 'en',
};

const PREFS_KEY = 'samkalp_askos_prefs';

export const loadPrefs = () => {
    try {
        const raw = localStorage.getItem(PREFS_KEY);
        return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : { ...DEFAULT_PREFS };
    } catch { return { ...DEFAULT_PREFS }; }
};

export const savePrefs = (prefs) => { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); };

export const applyPreferences = (prefs) => {
    const root = document.documentElement;
    root.setAttribute('data-askos-theme', prefs.theme);
    root.style.setProperty('--askos-accent', prefs.accent);
    root.style.setProperty('--askos-accent-light', prefs.accent + '22');
    root.style.setProperty('--askos-base-size', FONT_SIZES.find(f => f.key === prefs.fontSize)?.px || '16px');
    root.style.setProperty('--askos-line-height', LINE_SPACINGS.find(l => l.key === prefs.lineSpacing)?.lh || 1.5);
    if (prefs.dyslexiaFont) {
        root.style.setProperty('--askos-font-family', "'OpenDyslexic', 'Comic Sans MS', cursive");
        if (!document.getElementById('opendyslexic-css')) {
            const link = document.createElement('link');
            link.id = 'opendyslexic-css'; link.rel = 'stylesheet';
            link.href = 'https://fonts.cdnfonts.com/css/opendyslexic';
            document.head.appendChild(link);
        }
    } else {
        root.style.removeProperty('--askos-font-family');
    }
    document.body.classList.toggle('askos-reduce-motion', prefs.reduceMotion);
    document.body.classList.toggle('askos-dyslexia',       prefs.dyslexiaFont);
    document.body.classList.toggle('askos-focus-visible',  prefs.focusIndicators);
    root.setAttribute('data-askos-focus', prefs.focusIndicators ? 'visible' : 'default');
};

export const photoKey = (slug) => `samkalp_askos_photo_${slug}`;

// ── Markdown pre-processing ──────────────────────────────────────────────
export const normalizeMathForMarkdown = (text = '') => {
    if (!text) return '';
    const dec = (f = '') => { try { return decodeURIComponent(f); } catch { return f; } };
    return text
        .replace(/<div\s+[^>]*data-type=["']math-block["'][^>]*data-formula=["']([^"']+)["'][^>]*>\s*<\/div>/gi, (_, f) => `\n\n$$\n${dec(f)}\n$$\n\n`)
        .replace(/<span\s+[^>]*data-type=["']math-inline["'][^>]*data-formula=["']([^"']+)["'][^>]*>\s*<\/span>/gi, (_, f) => `$${dec(f)}$`);
};

export const stripFollowUps = (text) => text?.replace(/\*\*Want to explore further\?\*\*[\s\S]*$/, '').trim() || '';

export const toPlainText = (text) => (text || '')
    .replace(/\$\$[\s\S]*?\$\$/g, '').replace(/\$[^$\n]+\$/g, '')
    .replace(/\\\[[\s\S]*?\\\]/g, '').replace(/\\\([\s\S]*?\\\)/g, '')
    .replace(/```[\s\S]*?```/g, '').replace(/`[^`]*`/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')
    .replace(/#{1,6}\s/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<[^>]+>/g, '').replace(/[_~]/g, '').trim();

// ── Text-to-speech (browser SpeechSynthesis) ─────────────────────────────
export const stopSpeaking = () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };

const cleanForTTS = (text) => {
    if (!text) return '';
    return text
        .replace(/\$\$[\s\S]*?\$\$/g, '').replace(/\$[^$\n]+\$/g, '')
        .replace(/\\\[[\s\S]*?\\\]/g, '').replace(/\\\([\s\S]*?\\\)/g, '')
        .replace(/<[^>]*data-type=["']math[^"']*["'][^>]*>[\s\S]*?<\/[^>]+>/gi, '')
        .replace(/\b[A-Za-zη_]{1,6}\s*[=+\-*/^]\s*[A-Za-z0-9η_()\-+/*.^,\s]{1,40}/g, '')
        .replace(/\b\d+[\s]*[*/^][\s]*\d+/g, '').replace(/\([\d\s+\-*/^.]+\)/g, '')
        .replace(/```[\s\S]*?```/g, '').replace(/`[^`]+`/g, '')
        .replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')
        .replace(/#{1,6}\s/g, '').replace(/_([^_]+)_/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/<[^>]+>/g, '')
        .replace(/[*#~`\\]/g, '').replace(/\s{2,}/g, ' ').trim();
};

export const speakText = (text, langCode = 'en', speed = 1.0, voiceType = 'female', voiceName = null) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = cleanForTTS(text);
    if (!clean) return;
    const targetLang = LANG_MAP[langCode] || 'en-IN';
    const safeRate = Math.min(Math.max(Number(speed) || 1.0, 0.5), 2.0);

    const doSpeak = (allVoices) => {
        const abbrevSafe = clean.replace(/\b(e\.g|i\.e|fig|eq|approx|vs|no|dr|mr|mrs|prof|sr|jr)\./gi, m => m.replace('.', '##DOT##'));
        const sentences = (abbrevSafe.match(/[^.!?\n]+[.!?\n]*/g) || [abbrevSafe])
            .map(s => s.replace(/##DOT##/g, '.').trim()).filter(s => s.length > 1);

        let voice = null;
        if (voiceName) voice = allVoices.find(v => v.name === voiceName) || null;
        if (!voice) {
            const langBase = targetLang.split('-')[0];
            const INDO_ARYAN = new Set(['hi', 'bn', 'mr', 'gu', 'pa', 'ur']);
            let pool = allVoices.filter(v => v.lang === targetLang || v.lang.replace('_', '-') === targetLang || v.lang.replace('_', '-').startsWith(langBase + '-'));
            if (pool.length === 0 && INDO_ARYAN.has(langBase))
                pool = allVoices.filter(v => v.lang === 'hi-IN' || v.lang.replace('_', '-') === 'hi-IN' || v.name.toLowerCase().includes('hindi'));
            if (pool.length === 0)
                pool = allVoices.filter(v => { const l = v.lang.replace('_', '-'); const n = v.name.toLowerCase(); return l === 'en-IN' || n.includes('india') || n.includes('heera') || n.includes('ravi') || n.includes('priya'); });
            if (pool.length === 0) pool = allVoices.filter(v => v.lang.startsWith('en'));
            if (pool.length === 0) pool = allVoices;
            const isMale = (v) => /\b(male|ravi|prabhat|rishi|david|james|guy|mark|daniel)\b/i.test(v.name);
            const isFemale = (v) => /\b(female|heera|neerja|veena|lekha|aditi|zira|priya|tara|ananya|divya)\b/i.test(v.name);
            const isIndian = (v) => { const l = v.lang.replace('_', '-'); const n = v.name.toLowerCase(); return l.endsWith('-IN') || n.includes('india') || n.includes('hindi') || isMale(v) || isFemale(v); };
            const googleIndian = pool.filter(v => v.name.toLowerCase().includes('google') && isIndian(v));
            const indian = pool.filter(v => isIndian(v));
            const google = pool.filter(v => v.name.toLowerCase().includes('google'));
            const ranked = googleIndian.length ? googleIndian : indian.length ? indian : google.length ? google : pool;
            voice = voiceType === 'male'
                ? (ranked.find(v => isMale(v)) || pool.find(v => isMale(v)) || ranked[0] || pool[0])
                : (ranked.find(v => isFemale(v)) || pool.find(v => isFemale(v)) || ranked[0] || pool[0]);
        }
        sentences.forEach(s => {
            const u = new SpeechSynthesisUtterance(s);
            u.lang = voice?.lang || targetLang; u.rate = safeRate; u.pitch = 1.0;
            if (voice) u.voice = voice;
            window.speechSynthesis.speak(u);
        });
    };

    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { doSpeak(voices); }
    else {
        const onReady = () => { window.speechSynthesis.removeEventListener('voiceschanged', onReady); doSpeak(window.speechSynthesis.getVoices()); };
        window.speechSynthesis.addEventListener('voiceschanged', onReady);
    }
};
